import { createSelector } from '@ngrx/store';

import { selectCropAids } from 'src/app/crop-aid/store/crop-aid.selectors';
import { SalesOrderPeriod } from 'src/app/sales-order-period/models/sales-order-period.models';
import {
  selectSalesOrderPeriods,
  selectPastSalesOrderPeriods,
  selectFertilizationSalesOrderPeriods,
} from 'src/app/sales-order-period/store/sales-order-period.selectors';

import { CropAid } from '../../crop-aid/crop-aid.model';
import { AidAdvice } from '../../shared/models';

export const selectNitrogenEffectivity = ({
  cropAid,
  periodId,
  isAnExecutedTreatment,
}: {
  cropAid: CropAid;
  periodId: number;
  isAnExecutedTreatment: boolean;
}) =>
  createSelector(
    selectSalesOrderPeriods,
    selectPastSalesOrderPeriods,
    (salesOrderPeriods: SalesOrderPeriod[], pastSalesOrderPeriods: SalesOrderPeriod[]) => {
      if (!periodId || !cropAid?.fertilizer?.nitrogenEffectiveness) {
        return null;
      }
      const fertilizationSalesOrderPeriods =
        selectFertilizationSalesOrderPeriods.projector(salesOrderPeriods);
      const useSalesOrderPeriods = isAnExecutedTreatment
        ? pastSalesOrderPeriods
        : fertilizationSalesOrderPeriods;
      const difference = 12 - useSalesOrderPeriods.length;
      const salesOrderPeriod = useSalesOrderPeriods.find(({ id }) => id === periodId);
      if (!salesOrderPeriod) {
        return null;
      }
      const i = useSalesOrderPeriods.indexOf(salesOrderPeriod) + difference;
      if (i < 0) {
        return null;
      }
      return Object.values(cropAid.fertilizer.nitrogenEffectiveness)[i];
    },
  );

export const selectNitrogenEffectivityForAidAdvice = ({
  periodId,
  aidAdvice,
  isAnExecutedTreatment,
}: {
  periodId: number;
  aidAdvice: AidAdvice;
  isAnExecutedTreatment: boolean;
}) =>
  createSelector(
    selectSalesOrderPeriods,
    selectPastSalesOrderPeriods,
    selectCropAids,
    (
      salesOrderPeriods: SalesOrderPeriod[],
      pastSalesOrderPeriods: SalesOrderPeriod[],
      cropAids: CropAid[],
    ) =>
      aidAdvice.nitrogenEffectivity
        ? {
          nitrogenEffectivity: aidAdvice.nitrogenEffectivity,
          shouldUseNitrogenEffectivityManual: true,
        }
        : {
          nitrogenEffectivity: selectNitrogenEffectivity({
            cropAid: cropAids.find(({ id }) => id === aidAdvice.cropAidId),
            periodId,
            isAnExecutedTreatment,
          }).projector(salesOrderPeriods, pastSalesOrderPeriods),
          shouldUseNitrogenEffectivityManual: false,
        },
  );
